import { MatDialog } from "@angular/material/dialog";
import { CandidateRole } from "src/app/models/enum";
import {
	ManeuverOutcomeDraft,
	ManeuverOutcomeValue,
	OBComments,
	TemCompetencyOutcomeDraft,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";

export type OutcomeSlot = "pfPrimary" | "pm" | "pmPrimary" | "pf";

export const MANEUVER_OUTCOME_LABEL = "Maneuver Outcome";
export const MANEUVER_OUTCOME_NA_COMMENT = "Maneuver Outcome N/A";
export const MANEUVER_RATINGS_LABEL = "Maneuver Ratings";
export const TEM_SAFETY_OUTCOME_LABEL = "TEM Safety Outcome";
export const RATING_PER_COMPETENCY_LABEL = "Rating per Competency";

export const OUTCOME_COMMENT_PROMPT_RATINGS = [1, 2, 5] as const;

export function outcomeRatingRequiresComment(value: ManeuverOutcomeValue): boolean {
	return typeof value === "number" && OUTCOME_COMMENT_PROMPT_RATINGS.includes(value as 1 | 2 | 5);
}

/** Maneuver/TEM Outcome summary rows without remarks (ratings 3/4/N/A) are read-only in View/Edit modal. */
export function maneuverOutcomeSummaryCommentEditable(comment: OBComments): boolean {
	if (comment.isTemCompetencyOutcome) return !!comment.instructorComment?.trim();
	if (!comment.isManeuverOutcome) return true;
	if (comment.isDisabled || comment.remarks === "N/A") return false;
	return !!comment.instructorComment?.trim();
}

export function outcomeCommentField(slot: OutcomeSlot): keyof ManeuverOutcomeDraft {
	return `${slot}Comment` as keyof ManeuverOutcomeDraft;
}

const OUTCOME_SLOT_LABELS: Record<OutcomeSlot, string> = {
	pfPrimary: "PF(Primary)",
	pm: "PM",
	pmPrimary: "PM(Primary)",
	pf: "PF",
};

export function outcomeSlotLabel(slot: OutcomeSlot): string {
	return OUTCOME_SLOT_LABELS[slot];
}

export function outcomeSlotFromSummaryLabel(label: string): OutcomeSlot | null {
	const entry = (Object.entries(OUTCOME_SLOT_LABELS) as Array<[OutcomeSlot, string]>).find(
		([, slotLabel]) => slotLabel === label,
	);
	return entry?.[0] ?? null;
}

export function outcomeValueFromSummaryComment(comment: OBComments): ManeuverOutcomeValue | null {
	if (comment.isDisabled || comment.remarks === "N/A") return "na";
	if (typeof comment.rate === "number") return comment.rate as ManeuverOutcomeValue;
	return null;
}

/** Apply one summary/selection row back onto maneuverOutcomes draft. */
export function applyOutcomeSummaryCommentToDraft(
	outcomes: ManeuverOutcomeDraft[],
	comment: OBComments,
): ManeuverOutcomeDraft[] {
	const slot = outcomeSlotFromSummaryLabel(comment.ob ?? "");
	const value = outcomeValueFromSummaryComment(comment);
	if (!slot || value == null || !comment.maneuverCode) return outcomes;

	const idx = outcomes.findIndex((o) => o.maneuverCode === comment.maneuverCode);
	const base: ManeuverOutcomeDraft = idx >= 0 ? outcomes[idx] : { maneuverCode: comment.maneuverCode };
	const commentKey = outcomeCommentField(slot);
	const nextDraft: ManeuverOutcomeDraft = {
		...base,
		[slot]: value,
		[commentKey]: outcomeRatingRequiresComment(value) ? comment.instructorComment : undefined,
	};

	if (idx >= 0) {
		return outcomes.map((o, i) => (i === idx ? nextDraft : o));
	}
	return [...outcomes, nextDraft];
}

/** Apply one TEM Safety Outcome summary row back onto temCompetencyOutcomes draft. */
export function applyTemCompetencySummaryCommentToDraft(
	outcomes: TemCompetencyOutcomeDraft[],
	comment: OBComments,
): TemCompetencyOutcomeDraft[] {
	const slot = outcomeSlotFromSummaryLabel(comment.ob ?? "");
	const value = outcomeValueFromSummaryComment(comment);
	const competencyCode = comment.competencyCode ?? comment.maneuver;
	if (!slot || value == null || !competencyCode) return outcomes;

	const idx = outcomes.findIndex((o) => o.competencyCode === competencyCode);
	const base: TemCompetencyOutcomeDraft = idx >= 0 ? outcomes[idx] : { competencyCode };
	const commentKey = outcomeCommentField(slot);
	const nextDraft: TemCompetencyOutcomeDraft = {
		...base,
		[slot]: value,
		[commentKey as keyof TemCompetencyOutcomeDraft]: outcomeRatingRequiresComment(value)
			? comment.instructorComment
			: undefined,
	};

	if (idx >= 0) {
		return outcomes.map((o, i) => (i === idx ? nextDraft : o));
	}
	return [...outcomes, nextDraft];
}

export function buildManeuverOutcomeSummaryRowsForManeuver(
	outcome: ManeuverOutcomeDraft | undefined,
	maneuverLabel: string,
	candidateIds: { pf: string; pm: string },
	hasSecondCandidate = true,
): OBComments[] {
	if (!outcome?.maneuverCode) return [];

	const slots: OutcomeSlot[] = hasSecondCandidate ? ["pfPrimary", "pm", "pmPrimary", "pf"] : ["pfPrimary", "pm"];

	return slots
		.filter((slot) => outcome[slot] != null)
		.map((slot) => {
			const candidateId = outcomeSlotCandidateId(slot, candidateIds, hasSecondCandidate);
			if (!candidateId) return null;
			const label = OUTCOME_SLOT_LABELS[slot];
			const value = outcome[slot] as ManeuverOutcomeValue;
			const commentKey = outcomeCommentField(slot);
			return buildOutcomeSummaryComment({
				maneuverLabel,
				maneuverCode: outcome.maneuverCode,
				slot,
				slotLabel: label,
				value,
				instructorComment: (outcome[commentKey] as string | undefined) ?? "",
				candidateId,
			});
		})
		.filter((row): row is OBComments => row != null);
}

/** Stable key for one Maneuver Outcome slot in summary/history. */
export function outcomeSummaryRowKey(row: Pick<OBComments, "maneuverCode" | "candidateId" | "ob">): string {
	return `${row.maneuverCode ?? ""}::${row.candidateId ?? ""}::${row.ob ?? ""}`;
}

export function isReplaceableActiveOutcomeRow(row: OBComments): boolean {
	return !!(row.isManeuverOutcome || row.isTemCompetencyOutcome) && !row.isReset && !(row.isRepeat ?? false);
}

export function outcomeUpsertKey(row: OBComments): string | null {
	if (row.isManeuverOutcome) {
		return `mo::${outcomeSummaryRowKey(row)}`;
	}
	if (row.isTemCompetencyOutcome) {
		return `tem::${row.competencyCode ?? row.maneuver ?? ""}::${row.candidateId ?? ""}::${row.ob ?? ""}`;
	}
	return null;
}

export function buildLatestActiveOutcomeIndexMap(selectedOBs: OBComments[]): Map<string, number> {
	const latest = new Map<string, number>();
	for (let i = 0; i < selectedOBs.length; i++) {
		const row = selectedOBs[i];
		if (!isReplaceableActiveOutcomeRow(row)) continue;
		const key = outcomeUpsertKey(row);
		if (key) latest.set(key, i);
	}
	return latest;
}

function normalizeOutcomeComment(comment?: string): string {
	return (comment ?? "").trim().toUpperCase();
}

/** Dual-seat N/A outcomes share fixed text — never collapse them away. */
export function isTs1ManeuverOutcomeNaSummary(row: OBComments): boolean {
	if (!row.isManeuverOutcome) return false;
	if (row.isDisabled || row.remarks === "N/A") return true;
	return normalizeOutcomeComment(row.instructorComment) === normalizeOutcomeComment(MANEUVER_OUTCOME_NA_COMMENT);
}

export function outcomeRowsEquivalent(a: OBComments | undefined, b: OBComments): boolean {
	if (!a) return false;
	return (
		a.rate === b.rate &&
		!!a.isDisabled === !!b.isDisabled &&
		a.remarks === b.remarks &&
		normalizeOutcomeComment(a.instructorComment) === normalizeOutcomeComment(b.instructorComment)
	);
}

/**
 * Report preview: when PF/PM maneuver-rating rows carry the same rating + remarks for one
 * maneuver, keep only the row for the viewing candidate's assigned crew role.
 * Maneuver Outcome N/A rows are excluded — both seats must remain visible on dual reports.
 */
export function collapseEquivalentManeuverOutcomeComments(
	comments: OBComments[],
	options: { crewRoles: CandidateRole[]; candidateIndex: number },
): OBComments[] {
	const crewRole = options.crewRoles[options.candidateIndex];
	if (!crewRole) return comments;

	const suppressed = new Set<OBComments>();
	const buckets = new Map<string, OBComments[]>();

	for (const row of comments) {
		if (!row.isManeuverOutcome || !row.instructorComment?.trim()) continue;
		if (isTs1ManeuverOutcomeNaSummary(row)) continue;
		const key = `${row.candidateId ?? ""}::${row.maneuverCode ?? row.maneuver ?? ""}`;
		const bucket = buckets.get(key) ?? [];
		bucket.push(row);
		buckets.set(key, bucket);
	}

	for (const bucket of buckets.values()) {
		if (bucket.length < 2) continue;
		const primary = bucket.find((row) => row.candidateRole === crewRole);
		if (!primary) continue;
		for (const row of bucket) {
			if (row !== primary && outcomeRowsEquivalent(primary, row)) {
				suppressed.add(row);
			}
		}
	}

	return comments.filter((row) => !suppressed.has(row));
}

function latestOutcomeRowForSlot(selectedOBs: OBComments[], row: OBComments): OBComments | undefined {
	const key = outcomeSummaryRowKey(row);
	let latest: OBComments | undefined;
	let latestAt = -1;

	for (const candidate of selectedOBs) {
		if (!candidate.isManeuverOutcome || candidate.isRepeat || outcomeSummaryRowKey(candidate) !== key) continue;
		const at = candidate.createdAt ? new Date(candidate.createdAt).getTime() : 0;
		if (at >= latestAt) {
			latestAt = at;
			latest = candidate;
		}
	}

	return latest;
}

/** Upsert one Maneuver/TEM Outcome row — replaces the active row for the same slot. */
export function appendOutcomeToSelectedOBs(selectedOBs: OBComments[], row: OBComments): OBComments[] {
	const withTimestamp: OBComments = {
		...row,
		createdAt: row.createdAt ?? new Date().toISOString(),
	};
	const key = outcomeUpsertKey(withTimestamp);
	if (!key) return [...selectedOBs, withTimestamp];

	return [
		...selectedOBs.filter((existing) => !isReplaceableActiveOutcomeRow(existing) || outcomeUpsertKey(existing) !== key),
		withTimestamp,
	];
}

/**
 * Ensure draft selectedOBs contains the latest maneuverOutcomes state (draft reload recovery).
 * Does not replace history — only appends when the newest row for a slot does not match.
 */
export function ensureManeuverOutcomeHistoryInSelectedOBs(
	selectedOBs: OBComments[],
	maneuverOutcomes: ManeuverOutcomeDraft[],
	maneuverCode: string,
	maneuverLabel: string,
	candidateIds: { pf: string; pm: string },
	hasSecondCandidate = true,
): OBComments[] {
	const outcome = maneuverOutcomes.find((o) => o.maneuverCode === maneuverCode);
	const generated = buildManeuverOutcomeSummaryRowsForManeuver(
		outcome,
		maneuverLabel,
		candidateIds,
		hasSecondCandidate,
	);
	let merged = [...selectedOBs];

	for (const row of generated) {
		const latest = latestOutcomeRowForSlot(merged, row);
		if (outcomeRowsEquivalent(latest, row)) continue;
		merged = appendOutcomeToSelectedOBs(merged, row);
	}

	return merged;
}

export function outcomeSlotCandidateRole(slot: OutcomeSlot): CandidateRole {
	return slot === "pm" || slot === "pmPrimary" ? CandidateRole.PM : CandidateRole.PF;
}

/** Map OB panel column (left/right + crew role) to the Maneuver Outcome storage slot. */
export function outcomeSlotForObColumn(
	side: "left" | "right",
	candidateRole: CandidateRole,
	hasSecondCandidate: boolean,
): OutcomeSlot {
	if (side === "left") {
		return candidateRole === CandidateRole.PM ? "pm" : "pfPrimary";
	}
	if (!hasSecondCandidate) {
		return candidateRole === CandidateRole.PM ? "pm" : "pfPrimary";
	}
	return candidateRole === CandidateRole.PM ? "pmPrimary" : "pf";
}

/** True when that OB column's Maneuver Outcome is N/A — column must be non-editable. */
export function isObColumnLockedByOutcome(
	outcome: ManeuverOutcomeDraft | null | undefined,
	side: "left" | "right",
	candidateRole: CandidateRole,
	hasSecondCandidate: boolean,
): boolean {
	if (!outcome) return false;
	const slot = outcomeSlotForObColumn(side, candidateRole, hasSecondCandidate);
	return outcome[slot] === "na";
}

/**
 * Map an outcome storage slot to a candidate by carousel position (rating-form index).
 * Keys `pf` / `pm` are candidate index 0 and 1 ids (not crew-role holders).
 * - pfPrimary, pm → candidate index 0
 * - pf, pmPrimary → candidate index 1 (single-candidate: both columns → index 0)
 */
export function outcomeSlotCandidateId(
	slot: OutcomeSlot,
	candidateIds: { pf: string; pm: string },
	hasSecondCandidate = true,
): string {
	if (slot === "pfPrimary" || slot === "pm") {
		return candidateIds.pf || candidateIds.pm;
	}
	if (!hasSecondCandidate) {
		return candidateIds.pf || candidateIds.pm;
	}
	return candidateIds.pm;
}

/**
 * Each outcome row maps to one fixed candidate slot + role:
 * - Two candidates: pfPrimary/pm (left / candidate 1), pmPrimary/pf (right / candidate 2)
 * - One candidate: pfPrimary (left), pm (right) — same candidate in both columns
 */
export function isOutcomeSlotEnabled(
	slot: OutcomeSlot,
	crewRoles: CandidateRole[],
	hasSecondCandidate: boolean,
): boolean {
	const leftRole = crewRoles[0];
	const rightRole = crewRoles[1];

	switch (slot) {
		case "pfPrimary":
			return leftRole === CandidateRole.PF;
		case "pm":
			if (!hasSecondCandidate) return true;
			return leftRole === CandidateRole.PM;
		case "pmPrimary":
			return hasSecondCandidate && rightRole === CandidateRole.PM;
		case "pf":
			return hasSecondCandidate && rightRole === CandidateRole.PF;
		default:
			return false;
	}
}

export function outcomeRemarks(value: ManeuverOutcomeValue): string {
	if (value === "na") return "N/A";
	if (value === "inc") return "Inc.";
	return `Rating ${value}`;
}

export function buildOutcomeSummaryComment(params: {
	maneuverLabel: string;
	maneuverCode: string;
	slot: OutcomeSlot;
	slotLabel: string;
	value: ManeuverOutcomeValue;
	instructorComment: string;
	candidateId: string;
}): OBComments {
	const isNa = params.value === "na";
	return {
		maneuver: params.maneuverLabel,
		ob: params.slotLabel,
		action: true,
		remarks: outcomeRemarks(params.value),
		instructorComment: isNa ? MANEUVER_OUTCOME_NA_COMMENT : params.instructorComment.trim().toUpperCase(),
		candidateId: params.candidateId,
		maneuverCode: params.maneuverCode,
		rate: typeof params.value === "number" ? params.value : undefined,
		isDisabled: isNa,
		candidateRole: outcomeSlotCandidateRole(params.slot),
		isManeuverOutcome: true,
		createdAt: new Date().toISOString(),
	};
}

export function buildTemCompetencySummaryComment(params: {
	competencyCode: string;
	slot: OutcomeSlot;
	slotLabel: string;
	value: ManeuverOutcomeValue;
	instructorComment: string;
	candidateId: string;
}): OBComments {
	return {
		maneuver: params.competencyCode,
		ob: params.slotLabel,
		action: true,
		remarks: outcomeRemarks(params.value),
		instructorComment: params.instructorComment.trim().toUpperCase(),
		candidateId: params.candidateId,
		competencyCode: params.competencyCode,
		rate: typeof params.value === "number" ? params.value : undefined,
		isDisabled: params.value === "na",
		// Rating C is per-candidate — no PF/PM role on TEM draft rows.
		candidateRole: undefined,
		isTemCompetencyOutcome: true,
		createdAt: new Date().toISOString(),
	};
}

export function appendOutcomeSummaryRow(obComments: OBComments[], row: OBComments): void {
	obComments.push(row);
}

export function maneuverOutcomeHasActivity(outcome: ManeuverOutcomeDraft | null | undefined): boolean {
	if (!outcome) return false;
	const slots: OutcomeSlot[] = ["pfPrimary", "pm", "pmPrimary", "pf"];
	return slots.some((slot) => outcome[slot] != null);
}

/** Sidebar left-bar state derived from maneuver outcome selections (numeric rating > N/A > none). */
export type ManeuverSidebarStatus = "none" | "na" | "rated";

function requiredSidebarOutcomeSlots(hasSecondCandidate: boolean): OutcomeSlot[] {
	if (hasSecondCandidate) {
		return ["pfPrimary", "pm", "pmPrimary", "pf"];
	}
	return ["pfPrimary", "pm"];
}

export function resolveManeuverSidebarStatus(
	outcome: ManeuverOutcomeDraft | null | undefined,
	hasSecondCandidate: boolean,
): ManeuverSidebarStatus {
	if (!outcome) return "none";

	const slots = requiredSidebarOutcomeSlots(hasSecondCandidate);
	let hasRating = false;
	let allNa = true;

	for (const slot of slots) {
		const value = outcome[slot];
		if (value == null) return "none";
		if (typeof value === "number") hasRating = true;
		if (value !== "na") allNa = false;
	}

	if (hasRating) return "rated";
	if (allNa) return "na";
	return "none";
}

/** Outcome storage slots owned by one carousel candidate index. */
export function outcomeSlotsForCandidateIndex(candidateIndex: number, hasSecondCandidate: boolean): OutcomeSlot[] {
	if (!hasSecondCandidate) return ["pfPrimary", "pm"];
	return candidateIndex === 0 ? ["pfPrimary", "pm"] : ["pf", "pmPrimary"];
}

/** True when the maneuver outcome has a value on any slot for this candidate index. */
export function maneuverOutcomeHasActivityForCandidate(
	outcome: ManeuverOutcomeDraft | null | undefined,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): boolean {
	if (!outcome) return false;
	return outcomeSlotsForCandidateIndex(candidateIndex, hasSecondCandidate).some((slot) => outcome[slot] != null);
}

/** Clear one candidate's maneuver-outcome slots after Repeat. */
export function resetManeuverOutcomesForCandidateRepeat(
	outcomes: ManeuverOutcomeDraft[],
	maneuverCode: string,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): ManeuverOutcomeDraft[] {
	const slotsToClear = outcomeSlotsForCandidateIndex(candidateIndex, hasSecondCandidate);
	return outcomes.map((o) => {
		if (o.maneuverCode !== maneuverCode) return o;
		const next: ManeuverOutcomeDraft = { ...o };
		for (const slot of slotsToClear) {
			delete next[slot];
			const commentKey = outcomeCommentField(slot);
			delete next[commentKey];
			if (next.preNaObSnapshots) {
				const nextSnapshots = { ...next.preNaObSnapshots };
				delete nextSnapshots[slot];
				next.preNaObSnapshots = Object.keys(nextSnapshots).length > 0 ? nextSnapshots : undefined;
			}
		}
		if (!maneuverOutcomeHasActivity(next)) return { maneuverCode };
		return next;
	});
}

/** Clear all slot ratings/comments for one maneuver after Repeat (legacy maneuver-wide). */
export function resetManeuverOutcomesForRepeat(
	outcomes: ManeuverOutcomeDraft[],
	maneuverCode: string,
): ManeuverOutcomeDraft[] {
	return outcomes.map((o) => (o.maneuverCode === maneuverCode ? { maneuverCode } : o));
}

/** Mark persisted Maneuver Outcome summary rows as pre-repeat history for one candidate. */
export function markManeuverOutcomeSelectedOBsRepeatedForCandidate(
	selectedOBs: OBComments[],
	maneuverCode: string,
	candidateId: string,
): OBComments[] {
	return selectedOBs.map((row) =>
		row.isManeuverOutcome && row.maneuverCode === maneuverCode && row.candidateId === candidateId
			? { ...row, isRepeat: true }
			: row,
	);
}

/** Mark persisted Maneuver Outcome summary rows as pre-repeat history (legacy maneuver-wide). */
export function markManeuverOutcomeSelectedOBsRepeated(selectedOBs: OBComments[], maneuverCode: string): OBComments[] {
	return selectedOBs.map((row) =>
		row.isManeuverOutcome && row.maneuverCode === maneuverCode ? { ...row, isRepeat: true } : row,
	);
}

export function openOutcomeCommentModal(
	dialog: MatDialog,
	params: {
		maneuverLabel: string;
		slotLabel?: string;
		value: ManeuverOutcomeValue;
		existingComment?: string;
		typeLabel?: string;
	},
): Promise<{ comment: string } | null> {
	const rate = typeof params.value === "number" ? params.value : -1;
	const typeLabel = params.typeLabel ?? MANEUVER_RATINGS_LABEL;
	const dialogRef = dialog.open(CbtaModalComponent, {
		panelClass: "tem-comments-modal",
		height: "480px",
		width: "720px",
		hasBackdrop: true,
		data: {
			modalName: "temCommentsModal",
			showSwitch: false,
			commentAiAssist: true,
			modalTitle: "Remarks",
			modalBtnConfirmText: "Save",
			modalBtnCancelText: "Cancel",
			disableClose: true,
			initialComment: params.existingComment ?? "",
			temComment: {
				rating: rate,
				remarks: outcomeRemarks(params.value),
				maneuver: params.maneuverLabel,
				typeLabel,
				slotLabel: params.slotLabel,
			},
		},
	});

	return new Promise((resolve) => {
		dialogRef.afterClosed().subscribe((result: { close?: boolean; comment?: string } | undefined) => {
			if (result?.close && result.comment != null) {
				resolve({ comment: result.comment });
				return;
			}
			resolve(null);
		});
	});
}

/** Rebuild v3 maneuver/TEM outcome drafts from persisted selectedOBs (staging omits draft-only fields). */
export function restoreV3OutcomesFromSelectedOBs(selectedOBs: OBComments[]): {
	maneuverOutcomes: ManeuverOutcomeDraft[];
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
} {
	let maneuverOutcomes: ManeuverOutcomeDraft[] = [];
	let temCompetencyOutcomes: TemCompetencyOutcomeDraft[] = [];
	const latestIdx = buildLatestActiveOutcomeIndexMap(selectedOBs);

	for (const idx of latestIdx.values()) {
		const row = selectedOBs[idx];
		if (row.isManeuverOutcome) {
			maneuverOutcomes = applyOutcomeSummaryCommentToDraft(maneuverOutcomes, row);
		} else if (row.isTemCompetencyOutcome) {
			temCompetencyOutcomes = applyTemCompetencySummaryCommentToDraft(temCompetencyOutcomes, row);
		}
	}

	return { maneuverOutcomes, temCompetencyOutcomes };
}

/** Infer PF/PM from latest outcome summary rows when crewRoleAssignment is not persisted. */
export function inferCrewRoleForCandidateFromSelectedOBs(
	candidateId: string,
	selectedOBs: OBComments[],
): CandidateRole | null {
	if (!candidateId) return null;

	const latestIdx = buildLatestActiveOutcomeIndexMap(selectedOBs);
	let inferred: CandidateRole | null = null;

	for (const idx of latestIdx.values()) {
		const row = selectedOBs[idx];
		if (row.candidateId !== candidateId) continue;
		if (!row.isManeuverOutcome && !row.isTemCompetencyOutcome) continue;

		const slot = outcomeSlotFromSummaryLabel(row.ob ?? "");
		if (slot === "pm" || slot === "pmPrimary") {
			inferred = CandidateRole.PM;
		} else if (slot === "pfPrimary" || slot === "pf") {
			inferred = CandidateRole.PF;
		}
	}

	return inferred;
}
